import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateAppointmentStatus } from '../features/appointments';
import { useTranslation } from 'react-i18next';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Search } from 'lucide-react';
import {
  useBills,
  useCosts,
  useBillsStore,
  BillCard,
  BillModal,
  CostCard,
  CostModal,
  PaymentSummary,
} from '../features/bills';
import ChangelogModal, { CURRENT_CHANGELOG_VERSION } from '../components/ChangelogModal';
import { getDateRange } from '../features/bills/services/billService';

function Home() {
  const { t, i18n } = useTranslation('home');
  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'tr-TR';
  const location = useLocation();
  const navigate = useNavigate();

  // ── Feature hooks ──────────────────────────────────────────
  const {
    filteredBills,
    customers,
    employees,
    services,
    groupBillsByDate,
    handleOpenModal,
    handleDeleteBill,
    handleSaveBillData,
    handleCustomerChange,
    createBillFromAppointment,
  } = useBills();

  const { costs, loadCosts, groupCostsByDate, handleDeleteCost, handleSaveCost } = useCosts();

  // ── Bill store selectors ───────────────────────────────────
  const today             = useBillsStore(s => s.today);
  const searchTerm        = useBillsStore(s => s.searchTerm);
  const setSearchTerm     = useBillsStore(s => s.setSearchTerm);
  const modalIsOpen       = useBillsStore(s => s.modalIsOpen);
  const closeBillModal    = useBillsStore(s => s.closeBillModal);
  const isEditing         = useBillsStore(s => s.isEditing);
  const billForm          = useBillsStore(s => s.billForm);
  const setBillForm       = useBillsStore(s => s.setBillForm);
  const applyDiscount     = useBillsStore(s => s.applyDiscount);
  const setApplyDiscount  = useBillsStore(s => s.setApplyDiscount);
  const discountAmount    = useBillsStore(s => s.discountAmount);
  const setDiscountAmount = useBillsStore(s => s.setDiscountAmount);
  const selectedServices  = useBillsStore(s => s.selectedServices);
  const setSelectedServices = useBillsStore(s => s.setSelectedServices);
  const useLoyaltyPoints  = useBillsStore(s => s.useLoyaltyPoints);
  const setUseLoyaltyPoints = useBillsStore(s => s.setUseLoyaltyPoints);
  const pointsToUse       = useBillsStore(s => s.pointsToUse);
  const setPointsToUse    = useBillsStore(s => s.setPointsToUse);
  const isSaving          = useBillsStore(s => s.isSaving);
  const customerActivePackages = useBillsStore(s => s.customerActivePackages);
  const servicePackageUsage    = useBillsStore(s => s.servicePackageUsage);
  const setServicePackageUsage = useBillsStore(s => s.setServicePackageUsage);

  // ── Cost store selectors ───────────────────────────────────
  const costModalIsOpen = useBillsStore(s => s.costModalIsOpen);
  const closeCostModal  = useBillsStore(s => s.closeCostModal);
  const openCostModal   = useBillsStore(s => s.openCostModal);
  const selectedCostId  = useBillsStore(s => s.selectedCostId);
  const costForm        = useBillsStore(s => s.costForm);
  const setCostForm     = useBillsStore(s => s.setCostForm);
  const isCostSaving    = useBillsStore(s => s.isCostSaving);

  // ── Changelog ─────────────────────────────────────────────
  const [showChangelog, setShowChangelog] = useState(false);
  useEffect(() => {
    if (localStorage.getItem('lastSeenChangelogVersion') !== CURRENT_CHANGELOG_VERSION) {
      setShowChangelog(true);
    }
  }, []);

  // ── Appointment → Bill ────────────────────────────────────
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);
  // "Adisyon oluştur" ile gelen randevunun rollback bağlamı:
  // { appointmentId, previousStatus }. Adisyon kaydedilmeden akış başlatılamaz
  // veya modal kapatılırsa randevu previousStatus'a geri döndürülür.
  const apptCompletionRef = useRef(null);

  useEffect(() => {
    if (location.state?.createBillFromAppointment && location.state?.appointmentData) {
      setPendingAppointmentData(location.state.appointmentData);
      apptCompletionRef.current = location.state.appointmentCompletion || null;
      // Router state'ini temizle: sayfa yenilenince/geri gelince fatura tekrar açılmasın
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  // Randevuyu eski durumuna geri döndür (rollback) ve bağlamı temizle
  const revertAppointmentCompletion = useCallback(async () => {
    const ctx = apptCompletionRef.current;
    if (!ctx) return;
    apptCompletionRef.current = null;
    try {
      await updateAppointmentStatus(ctx.appointmentId, ctx.previousStatus);
      toast.info(t('toasts.appointmentReverted'));
    } catch (err) {
      console.error('[Home] appointment revert error:', err);
    }
  }, [t]);

  useEffect(() => {
    if (pendingAppointmentData && customers.length > 0 && employees.length > 0) {
      const opened = createBillFromAppointment(pendingAppointmentData);
      setPendingAppointmentData(null);
      // Adisyon akışı başlayamadıysa (müşteri/çalışan yok vb.) randevuyu geri al
      if (!opened) revertAppointmentCompletion();
    }
  }, [pendingAppointmentData, customers.length, employees.length, createBillFromAppointment, revertAppointmentCompletion]);

  // Adisyon modalı kullanıcı tarafından KAYDEDİLMEDEN kapatılırsa randevuyu geri al.
  // (Başarılı kayıtta modal store üzerinden programatik kapanır, onClose tetiklenmez.)
  const handleBillModalClose = useCallback(() => {
    revertAppointmentCompletion();
    closeBillModal();
  }, [revertAppointmentCompletion, closeBillModal]);

  // Adisyon kaydı: başarılıysa modal kapanmış olur → rollback bağlamını temizle
  // ki randevu 'completed' olarak kalsın.
  const handleBillSave = useCallback(async () => {
    await handleSaveBillData();
    if (!useBillsStore.getState().modalIsOpen) {
      apptCompletionRef.current = null;
    }
  }, [handleSaveBillData]);

  // ── Costs initial load ────────────────────────────────────
  useEffect(() => { loadCosts(); }, [loadCosts]);

  // ── Date range helpers ────────────────────────────────────
  const dateRange = getDateRange();
  const pastBills = filteredBills.filter(b => dateRange.includes(b.date) && b.date !== today);
  const pastCosts = costs.filter(c => dateRange.includes(c.date) && c.date !== today);

  return (
    <div className="p-6">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover draggable />

      <PaymentSummary bills={filteredBills} costs={costs} />

      <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-6"></div>

      {/* Arama ve Butonlar */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="relative w-full sm:max-w-2xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder={t('page.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-md hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 w-full sm:w-auto"
          >
            {t('page.addBill')}
          </button>
          <button
            onClick={() => openCostModal()}
            className="flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md hover:from-red-600 hover:to-pink-600 transition-all duration-300 w-full sm:w-auto"
          >
            {t('page.addCost')}
          </button>
        </div>
      </div>

      {/* Bugünün Adisyonları */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('page.todaysBills')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBills.filter(b => b.date === today).map((bill) => (
            <BillCard key={bill.id} bill={bill} onEdit={handleOpenModal} onDelete={handleDeleteBill} />
          ))}
          {filteredBills.filter(b => b.date === today).length === 0 && (
            <p className="text-gray-500 col-span-full text-center py-4">{t('page.noBillsToday')}</p>
          )}
        </div>
      </div>

      {/* Bugünün Giderleri */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('page.todaysCosts')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {costs.filter(c => c.date === today).map((cost) => (
            <CostCard key={cost.id} cost={cost} onEdit={openCostModal} onDelete={handleDeleteCost} />
          ))}
          {costs.filter(c => c.date === today).length === 0 && (
            <p className="text-gray-500 col-span-full text-center py-4">{t('page.noCostsToday')}</p>
          )}
        </div>
      </div>

      {/* Geçmiş Adisyonlar — sadece admin */}
      {pastBills.length > 0 && (
        <>
          <div className="border-t border-gray-200 my-8"></div>
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('page.pastBills')}</h2>
            {Object.entries(groupBillsByDate(pastBills))
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([date, dateBills]) => (
                <div key={date} className="mb-8">
                  <h3 className="text-lg font-medium mb-4 text-gray-700">
                    {new Date(date).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {dateBills.map((bill) => (
                      <BillCard key={bill.id} bill={bill} onEdit={handleOpenModal} onDelete={handleDeleteBill} />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </>
      )}

      {/* Geçmiş Giderler — sadece admin */}
      {pastCosts.length > 0 && (
        <>
          <div className="border-t border-gray-200 my-8"></div>
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('page.pastCosts')}</h2>
            {Object.entries(groupCostsByDate(pastCosts))
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([date, dateCosts]) => (
                <div key={date} className="mb-8">
                  <h3 className="text-lg font-medium mb-4 text-gray-700">
                    {new Date(date).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {dateCosts.map((cost) => (
                      <CostCard key={cost.id} cost={cost} onEdit={openCostModal} onDelete={handleDeleteCost} />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </>
      )}

      {/* BillModal */}
      <BillModal
        isOpen={modalIsOpen}
        onClose={handleBillModalClose}
        billForm={billForm}
        setBillForm={setBillForm}
        onBillFormChange={(field, value) => {
          setBillForm(prev => ({ ...prev, [field]: value }));
          if (field === 'customerId') handleCustomerChange(value);
        }}
        onSave={handleBillSave}
        isEditing={isEditing}
        applyDiscount={applyDiscount}
        setApplyDiscount={setApplyDiscount}
        discountAmount={discountAmount}
        setDiscountAmount={setDiscountAmount}
        selectedServices={selectedServices}
        setSelectedServices={setSelectedServices}
        customers={customers}
        employees={employees}
        services={services}
        useLoyaltyPoints={useLoyaltyPoints}
        setUseLoyaltyPoints={setUseLoyaltyPoints}
        pointsToUse={pointsToUse}
        setPointsToUse={setPointsToUse}
        isSaving={isSaving}
        customerActivePackages={customerActivePackages}
        servicePackageUsage={servicePackageUsage}
        setServicePackageUsage={setServicePackageUsage}
      />

      {/* CostModal */}
      <CostModal
        isOpen={costModalIsOpen}
        onClose={closeCostModal}
        costForm={costForm}
        setCostForm={setCostForm}
        onSave={handleSaveCost}
        isEditing={!!selectedCostId}
        isSaving={isCostSaving}
      />

      {/* ChangelogModal */}
      <ChangelogModal
        isOpen={showChangelog}
        onClose={() => setShowChangelog(false)}
        onDontShowAgain={() => {
          localStorage.setItem('lastSeenChangelogVersion', CURRENT_CHANGELOG_VERSION);
          setShowChangelog(false);
        }}
      />
    </div>
  );
}

export default Home;
